import {Voivodeship} from "../../../app/types/locationTypes";

/** Raw React Router params for offer list (location may be model when make is set). */
export interface IOfferListRawPathParams {
    type?: string;
    location?: string;
    subLocation?: string;
    subSubLocation?: string;
}

/** Normalized path semantics after voivodeship-vs-model disambiguation. */
export interface IParsedOfferListPath {
    type?: string;
    model?: string;
    location?: string;
    subLocation?: string;
}

const VOIVODESHIP_SLUGS = new Set(Object.values(Voivodeship) as string[]);

export const isVoivodeshipSlug = (slug?: string): boolean => Boolean(slug && VOIVODESHIP_SLUGS.has(slug.toLowerCase()));

/** ASCII path slug for car model (V90 → v90, XC 60 → xc-60). */
export const toModelSlug = (model: string): string =>
    model
        .trim()
        .toLowerCase()
        .replace(/ą/g, "a")
        .replace(/ć/g, "c")
        .replace(/ę/g, "e")
        .replace(/ł/g, "l")
        .replace(/ń/g, "n")
        .replace(/ó/g, "o")
        .replace(/ś/g, "s")
        .replace(/ź|ż/g, "z")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

/**
 * When make (`type`) is present, the first segment after make is either a voivodeship
 * or a model slug. Without make, segments are always location/city (no path model).
 */
export const parseOfferListPath = (params: IOfferListRawPathParams): IParsedOfferListPath => {
    const type = params.type || undefined;
    const seg1 = params.location || undefined;
    const seg2 = params.subLocation || undefined;
    const seg3 = params.subSubLocation || undefined;

    if (!type) {
        return {
            type: undefined,
            model: undefined,
            location: seg1,
            subLocation: seg2
        };
    }

    if (!seg1) {
        return {type, model: undefined, location: undefined, subLocation: undefined};
    }

    if (isVoivodeshipSlug(seg1)) {
        return {
            type,
            model: undefined,
            location: seg1.toLowerCase(),
            subLocation: seg2
        };
    }

    return {
        type,
        model: seg1,
        location: seg2 ? (isVoivodeshipSlug(seg2) ? seg2.toLowerCase() : seg2) : undefined,
        subLocation: seg3
    };
};

/**
 * Map logical offer-list params (model + location) onto React Router path slots.
 * With model: location=modelSlug, subLocation=woj, subSubLocation=city.
 */
export const buildOfferListPathParams = (params: {
    type?: string;
    model?: string;
    location?: string;
    subLocation?: string;
}): IOfferListRawPathParams => {
    const type = params.type || undefined;
    const modelSlug = params.model ? toModelSlug(params.model) : undefined;

    if (modelSlug && type) {
        return {
            type,
            location: modelSlug,
            subLocation: params.location || undefined,
            subSubLocation: params.subLocation || undefined
        };
    }

    return {
        type,
        location: params.location || undefined,
        subLocation: params.subLocation || undefined,
        subSubLocation: undefined
    };
};
